import React, { useState } from 'react';
import { Card, Table, Button, Space, Tag, Typography, Spin, Alert, Modal, Form, Input, InputNumber, Select, Switch, message, Tooltip, Popover, List, Empty } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, AppstoreAddOutlined, SyncOutlined, CloudUploadOutlined, CheckCircleOutlined, ExclamationCircleOutlined, ClockCircleOutlined, SettingOutlined } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAuth } from '../../hooks/useAuth';
import {
    useGetAdminAddonsQuery,
    useCreateAdminAddonMutation,
    useUpdateAdminAddonMutation,
    useDeleteAdminAddonMutation,
    useSyncAddonToStripeMutation,
    useSyncAllAddonsToStripeMutation,
    useGetAdminSubscriptionPlansQuery,
    useGetAdminFeaturesQuery,
    useGetAdminAddonEntitlementsQuery,
    useCreateAdminAddonEntitlementMutation,
    useUpdateAdminAddonEntitlementMutation,
    useDeleteAdminAddonEntitlementMutation,
    IAddon,
    ICreateAddonDto,
    AddonType,
    IAddonEntitlement,
    ValueType,
} from '../../services/nestjs-base-api';

const { Title } = Typography;
const { TextArea } = Input;

export const AdminAddonsPage: React.FC = () => {
    const intl = useIntl();
    const { user } = useAuth();
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [editingAddon, setEditingAddon] = useState<IAddon | null>(null);
    const [isEntitlementsModalOpen, setIsEntitlementsModalOpen] = useState(false);
    const [selectedAddonForEntitlements, setSelectedAddonForEntitlements] = useState<IAddon | null>(null);
    const [isAddEntitlementModalOpen, setIsAddEntitlementModalOpen] = useState(false);
    const [editingEntitlement, setEditingEntitlement] = useState<IAddonEntitlement | null>(null);
    const [form] = Form.useForm();
    const [entitlementForm] = Form.useForm();

    const { data: addons, isLoading, error } = useGetAdminAddonsQuery({
        jwtToken: user?.jwtToken || '',
    });

    const { data: plans } = useGetAdminSubscriptionPlansQuery({
        jwtToken: user?.jwtToken || '',
    });

    const { data: features } = useGetAdminFeaturesQuery({
        jwtToken: user?.jwtToken || '',
    });

    const { data: entitlements } = useGetAdminAddonEntitlementsQuery(
        { jwtToken: user?.jwtToken || '', addonId: selectedAddonForEntitlements?.id || '' },
        { skip: !selectedAddonForEntitlements }
    );

    const [createAddon, { isLoading: isCreating }] = useCreateAdminAddonMutation();
    const [updateAddon, { isLoading: isUpdating }] = useUpdateAdminAddonMutation();
    const [deleteAddon] = useDeleteAdminAddonMutation();
    const [syncAddonToStripe, { isLoading: isSyncing }] = useSyncAddonToStripeMutation();
    const [syncAllAddonsToStripe, { isLoading: isSyncingAll }] = useSyncAllAddonsToStripeMutation();
    const [createEntitlement, { isLoading: isCreatingEntitlement }] = useCreateAdminAddonEntitlementMutation();
    const [updateEntitlement, { isLoading: isUpdatingEntitlement }] = useUpdateAdminAddonEntitlementMutation();
    const [deleteEntitlement] = useDeleteAdminAddonEntitlementMutation();

    const handleCreate = () => {
        setEditingAddon(null);
        form.resetFields();
        form.setFieldsValue({
            currency: 'USD',
            isPublic: true,
            isActive: true,
            addonType: 'one_time',
        });
        setIsCreateModalOpen(true);
    };

    const handleEdit = (addon: IAddon) => {
        setEditingAddon(addon);
        form.setFieldsValue({
            code: addon.code,
            name: addon.name,
            description: addon.description,
            addonType: addon.addonType,
            billingPeriod: addon.billingPeriod,
            priceCents: addon.priceCents,
            currency: addon.currency,
            isPublic: addon.isPublic,
            isActive: addon.isActive,
            compatiblePlans: addon.compatiblePlans,
        });
        setIsCreateModalOpen(true);
    };

    const handleDelete = (addon: IAddon) => {
        Modal.confirm({
            title: intl.formatMessage({ id: 'addons.deleteConfirmTitle', defaultMessage: 'Delete Add-on?' }),
            content: intl.formatMessage(
                { id: 'addons.deleteConfirmContent', defaultMessage: 'Are you sure you want to delete "{name}"?' },
                { name: addon.name }
            ),
            okText: intl.formatMessage({ id: 'common.delete', defaultMessage: 'Delete' }),
            okType: 'danger',
            cancelText: intl.formatMessage({ id: 'common.cancel', defaultMessage: 'Cancel' }),
            onOk: async () => {
                try {
                    await deleteAddon({
                        jwtToken: user?.jwtToken || '',
                        id: addon.id,
                    }).unwrap();
                    message.success(intl.formatMessage({ id: 'addons.deleteSuccess', defaultMessage: 'Add-on deleted successfully' }));
                } catch (error) {
                    message.error(intl.formatMessage({ id: 'addons.deleteError', defaultMessage: 'Failed to delete add-on' }));
                }
            },
        });
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            if (editingAddon) {
                await updateAddon({
                    jwtToken: user?.jwtToken || '',
                    id: editingAddon.id,
                    payload: values,
                }).unwrap();
                message.success(intl.formatMessage({ id: 'addons.updateSuccess', defaultMessage: 'Add-on updated successfully' }));
            } else {
                await createAddon({
                    jwtToken: user?.jwtToken || '',
                    payload: values as ICreateAddonDto,
                }).unwrap();
                message.success(intl.formatMessage({ id: 'addons.createSuccess', defaultMessage: 'Add-on created successfully' }));
            }

            setIsCreateModalOpen(false);
            form.resetFields();
        } catch (error) {
            message.error(intl.formatMessage({
                id: editingAddon ? 'addons.updateError' : 'addons.createError',
                defaultMessage: editingAddon ? 'Failed to update add-on' : 'Failed to create add-on'
            }));
        }
    };

    // Entitlements handlers
    const handleManageEntitlements = (addon: IAddon) => {
        setSelectedAddonForEntitlements(addon);
        setIsEntitlementsModalOpen(true);
    };

    const handleAddEntitlement = () => {
        setEditingEntitlement(null);
        entitlementForm.resetFields();
        entitlementForm.setFieldsValue({
            valueType: 'set',
        });
        setIsAddEntitlementModalOpen(true);
    };

    const handleEditEntitlement = (entitlement: IAddonEntitlement) => {
        setEditingEntitlement(entitlement);
        entitlementForm.setFieldsValue({
            featureId: entitlement.featureId,
            value: entitlement.value,
            valueType: entitlement.valueType,
        });
        setIsAddEntitlementModalOpen(true);
    };

    const handleDeleteEntitlement = async (entitlement: IAddonEntitlement) => {
        if (!selectedAddonForEntitlements) return;

        Modal.confirm({
            title: 'Remove Feature?',
            content: `Are you sure you want to remove "${entitlement.feature?.name || 'this feature'}" from this add-on?`,
            okText: 'Remove',
            okType: 'danger',
            onOk: async () => {
                try {
                    await deleteEntitlement({
                        jwtToken: user?.jwtToken || '',
                        addonId: selectedAddonForEntitlements.id,
                        entitlementId: entitlement.id,
                    }).unwrap();
                    message.success('Feature removed from add-on');
                } catch (error) {
                    message.error('Failed to remove feature');
                }
            },
        });
    };

    const handleSubmitEntitlement = async () => {
        if (!selectedAddonForEntitlements) return;

        try {
            const values = await entitlementForm.validateFields();

            if (editingEntitlement) {
                await updateEntitlement({
                    jwtToken: user?.jwtToken || '',
                    addonId: selectedAddonForEntitlements.id,
                    entitlementId: editingEntitlement.id,
                    payload: { value: values.value, valueType: values.valueType },
                }).unwrap();
                message.success('Feature entitlement updated');
            } else {
                await createEntitlement({
                    jwtToken: user?.jwtToken || '',
                    addonId: selectedAddonForEntitlements.id,
                    payload: {
                        featureId: values.featureId,
                        value: values.value,
                        valueType: values.valueType,
                    },
                }).unwrap();
                message.success('Feature added to add-on');
            }

            setIsAddEntitlementModalOpen(false);
            entitlementForm.resetFields();
        } catch (error: any) {
            message.error(error?.data?.message || 'Failed to save feature entitlement');
        }
    };

    // Stripe sync handlers
    const handleSyncAddon = async (addon: IAddon) => {
        try {
            const result = await syncAddonToStripe({
                jwtToken: user?.jwtToken || '',
                id: addon.id,
            }).unwrap();

            if (result.success) {
                message.success(`Add-on "${addon.name}" synced to Stripe successfully`);
            } else {
                message.error(`Sync failed: ${result.error}`);
            }
        } catch (error: any) {
            message.error(error?.data?.message || 'Failed to sync add-on to Stripe');
        }
    };

    const handleSyncAllAddons = async () => {
        Modal.confirm({
            title: 'Sync All Add-ons to Stripe',
            content: 'This will sync all add-ons to Stripe. Existing Stripe products will be updated. Continue?',
            okText: 'Sync All',
            onOk: async () => {
                try {
                    const result = await syncAllAddonsToStripe({
                        jwtToken: user?.jwtToken || '',
                    }).unwrap();
                    message.success(`Synced ${result.synced} add-ons to Stripe (${result.failed} failed)`);
                } catch (error: any) {
                    message.error(error?.data?.message || 'Failed to sync add-ons to Stripe');
                }
            },
        });
    };

    // Get Stripe sync status tag
    const getSyncStatusTag = (addon: IAddon) => {
        const status = addon.stripeSyncStatus || 'pending';
        const syncedAt = addon.stripeSyncedAt ? new Date(addon.stripeSyncedAt).toLocaleString() : null;

        switch (status) {
            case 'synced':
                return (
                    <Tooltip title={syncedAt ? `Last synced: ${syncedAt}` : 'Synced'}>
                        <Tag color="green" icon={<CheckCircleOutlined />}>Synced</Tag>
                    </Tooltip>
                );
            case 'failed':
                return (
                    <Popover
                        content={addon.stripeSyncError || 'Unknown error'}
                        title="Sync Error"
                        trigger="hover"
                    >
                        <Tag color="red" icon={<ExclamationCircleOutlined />}>Failed</Tag>
                    </Popover>
                );
            case 'pending':
            default:
                return (
                    <Tooltip title="Not yet synced to Stripe">
                        <Tag color="orange" icon={<ClockCircleOutlined />}>Pending</Tag>
                    </Tooltip>
                );
        }
    };

    // Get addon type display
    const getAddonTypeTag = (type: AddonType) => {
        const colors: Record<AddonType, string> = {
            one_time: 'blue',
            recurring: 'purple',
            usage_pack: 'cyan',
        };
        const labels: Record<AddonType, string> = {
            one_time: 'One-Time',
            recurring: 'Recurring',
            usage_pack: 'Usage Pack',
        };
        return <Tag color={colors[type]}>{labels[type]}</Tag>;
    };

    // Get value type display
    const getValueTypeTag = (valueType: ValueType) => {
        const colors: Record<ValueType, string> = {
            set: 'blue',
            add: 'green',
            upgrade: 'purple',
        };
        const labels: Record<ValueType, string> = {
            set: 'Set',
            add: 'Add',
            upgrade: 'Upgrade',
        };
        return <Tag color={colors[valueType]}>{labels[valueType]}</Tag>;
    };

    // Get features that haven't been assigned to this addon yet
    const availableFeatures = features?.filter(
        f => !entitlements?.some(e => e.featureId === f.id)
    ) || [];

    const columns = [
        {
            title: <FormattedMessage id="addons.code" defaultMessage="Code" />,
            dataIndex: 'code',
            key: 'code',
        },
        {
            title: <FormattedMessage id="addons.name" defaultMessage="Name" />,
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: <FormattedMessage id="addons.type" defaultMessage="Type" />,
            dataIndex: 'addonType',
            key: 'addonType',
            render: (type: AddonType) => getAddonTypeTag(type),
        },
        {
            title: <FormattedMessage id="addons.price" defaultMessage="Price" />,
            dataIndex: 'priceCents',
            key: 'priceCents',
            render: (priceCents: number, record: IAddon) =>
                `${record.currency} ${(priceCents / 100).toFixed(2)}`,
        },
        {
            title: <FormattedMessage id="addons.billingPeriod" defaultMessage="Billing Period" />,
            dataIndex: 'billingPeriod',
            key: 'billingPeriod',
            render: (period: string | undefined) => period ? (
                <Tag color="blue">{period}</Tag>
            ) : (
                <Tag color="default">N/A</Tag>
            ),
        },
        {
            title: <FormattedMessage id="addons.status" defaultMessage="Status" />,
            key: 'status',
            render: (_: any, record: IAddon) => (
                <Space>
                    <Tag color={record.isActive ? 'green' : 'red'}>
                        {record.isActive ? 'Active' : 'Inactive'}
                    </Tag>
                    {record.isPublic && <Tag color="blue">Public</Tag>}
                </Space>
            ),
        },
        {
            title: 'Stripe Sync',
            key: 'stripeSync',
            render: (_: any, record: IAddon) => getSyncStatusTag(record),
        },
        {
            title: <FormattedMessage id="common.actions" defaultMessage="Actions" />,
            key: 'actions',
            render: (_: any, record: IAddon) => (
                <Space>
                    <Tooltip title="Sync to Stripe">
                        <Button
                            size="small"
                            icon={<SyncOutlined spin={isSyncing} style={{ marginRight: 4 }} />}
                            onClick={() => handleSyncAddon(record)}
                            disabled={isSyncing}
                        >
                            Sync
                        </Button>
                    </Tooltip>
                    <Button
                        size="small"
                        icon={<SettingOutlined style={{ marginRight: 4 }} />}
                        onClick={() => handleManageEntitlements(record)}
                    >
                        Features
                    </Button>
                    <Button
                        size="small"
                        icon={<EditOutlined style={{ marginRight: 4 }} />}
                        onClick={() => handleEdit(record)}
                    >
                        <FormattedMessage id="common.edit" defaultMessage="Edit" />
                    </Button>
                    <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined style={{ marginRight: 4 }} />}
                        onClick={() => handleDelete(record)}
                    >
                        <FormattedMessage id="common.delete" defaultMessage="Delete" />
                    </Button>
                </Space>
            ),
        },
    ];

    // Watch addonType for conditional billing period field
    const addonType = Form.useWatch('addonType', form);

    if (isLoading) {
        return (
            <div style={{ padding: '24px', textAlign: 'center' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: '24px' }}>
                <Alert
                    message="Error loading add-ons"
                    description="Unable to fetch add-ons. Please try again later."
                    type="error"
                    showIcon
                />
            </div>
        );
    }

    return (
        <div style={{ padding: '24px' }}>
            <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <Title level={3} style={{ margin: 0 }}>
                        <AppstoreAddOutlined style={{ marginRight: 8 }} />
                        <FormattedMessage id="addons.title" defaultMessage="Add-ons" />
                    </Title>
                    <Space>
                        <Button
                            icon={<CloudUploadOutlined style={{ marginRight: 4 }} />}
                            onClick={handleSyncAllAddons}
                            loading={isSyncingAll}
                        >
                            Sync All to Stripe
                        </Button>
                        <Button
                            type="primary"
                            icon={<PlusOutlined style={{ marginRight: 4 }} />}
                            onClick={handleCreate}
                        >
                            <FormattedMessage id="addons.create" defaultMessage="Create Add-on" />
                        </Button>
                    </Space>
                </div>

                <Table
                    dataSource={addons || []}
                    columns={columns}
                    rowKey="id"
                    pagination={false}
                />
            </Card>

            {/* Create/Edit Addon Modal */}
            <Modal
                title={
                    editingAddon ? (
                        <FormattedMessage id="addons.editTitle" defaultMessage="Edit Add-on" />
                    ) : (
                        <FormattedMessage id="addons.createTitle" defaultMessage="Create Add-on" />
                    )
                }
                open={isCreateModalOpen}
                onOk={handleSubmit}
                onCancel={() => {
                    setIsCreateModalOpen(false);
                    form.resetFields();
                }}
                confirmLoading={isCreating || isUpdating}
                width={600}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="code"
                        label={<FormattedMessage id="addons.code" defaultMessage="Code" />}
                        rules={[{ required: true, message: intl.formatMessage({ id: 'addons.codeRequired', defaultMessage: 'Code is required' }) }]}
                    >
                        <Input
                            placeholder={intl.formatMessage({ id: 'addons.codePlaceholder', defaultMessage: 'e.g., additional_brand' })}
                            disabled={!!editingAddon}
                        />
                    </Form.Item>

                    <Form.Item
                        name="name"
                        label={<FormattedMessage id="addons.name" defaultMessage="Name" />}
                        rules={[{ required: true, message: intl.formatMessage({ id: 'addons.nameRequired', defaultMessage: 'Name is required' }) }]}
                    >
                        <Input placeholder={intl.formatMessage({ id: 'addons.namePlaceholder', defaultMessage: 'e.g., Additional Brand' })} />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label={<FormattedMessage id="addons.description" defaultMessage="Description" />}
                    >
                        <TextArea rows={3} />
                    </Form.Item>

                    <Form.Item
                        name="addonType"
                        label={<FormattedMessage id="addons.type" defaultMessage="Type" />}
                        rules={[{ required: true }]}
                    >
                        <Select>
                            <Select.Option value="one_time">One-Time Purchase</Select.Option>
                            <Select.Option value="recurring">Recurring</Select.Option>
                            <Select.Option value="usage_pack">Usage Pack</Select.Option>
                        </Select>
                    </Form.Item>

                    {addonType === 'recurring' && (
                        <Form.Item
                            name="billingPeriod"
                            label={<FormattedMessage id="addons.billingPeriod" defaultMessage="Billing Period" />}
                            rules={[{ required: addonType === 'recurring', message: 'Billing period is required for recurring add-ons' }]}
                        >
                            <Select>
                                <Select.Option value="monthly">Monthly</Select.Option>
                                <Select.Option value="annual">Annual</Select.Option>
                                <Select.Option value="quarterly">Quarterly</Select.Option>
                            </Select>
                        </Form.Item>
                    )}

                    <Form.Item
                        name="priceCents"
                        label={<FormattedMessage id="addons.priceCents" defaultMessage="Price (in cents)" />}
                        rules={[{ required: true }]}
                    >
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        name="currency"
                        label={<FormattedMessage id="addons.currency" defaultMessage="Currency" />}
                        initialValue="USD"
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name="compatiblePlans"
                        label={<FormattedMessage id="addons.compatiblePlans" defaultMessage="Compatible Plans" />}
                        extra="Leave empty to allow with all plans"
                    >
                        <Select
                            mode="multiple"
                            placeholder="Select compatible plans (optional)"
                            allowClear
                        >
                            {plans?.map(plan => (
                                <Select.Option key={plan.id} value={plan.id}>
                                    {plan.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="isPublic"
                        label={<FormattedMessage id="addons.isPublic" defaultMessage="Public" />}
                        valuePropName="checked"
                        initialValue={true}
                    >
                        <Switch />
                    </Form.Item>

                    <Form.Item
                        name="isActive"
                        label={<FormattedMessage id="addons.isActive" defaultMessage="Active" />}
                        valuePropName="checked"
                        initialValue={true}
                    >
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Entitlements Modal */}
            <Modal
                title={
                    <span>
                        <SettingOutlined style={{ marginRight: 8 }} />
                        Features for: {selectedAddonForEntitlements?.name}
                    </span>
                }
                open={isEntitlementsModalOpen}
                onCancel={() => {
                    setIsEntitlementsModalOpen(false);
                    setSelectedAddonForEntitlements(null);
                }}
                footer={[
                    <Button key="close" onClick={() => setIsEntitlementsModalOpen(false)}>
                        Close
                    </Button>,
                ]}
                width={700}
            >
                <div style={{ marginBottom: 16 }}>
                    <Button
                        type="primary"
                        icon={<PlusOutlined style={{ marginRight: 4 }} />}
                        onClick={handleAddEntitlement}
                        disabled={availableFeatures.length === 0}
                    >
                        Add Feature
                    </Button>
                    {availableFeatures.length === 0 && features && features.length > 0 && (
                        <span style={{ marginLeft: 12, color: '#999' }}>
                            All features assigned
                        </span>
                    )}
                    {(!features || features.length === 0) && (
                        <span style={{ marginLeft: 12, color: '#ff4d4f' }}>
                            No features defined. Create features first.
                        </span>
                    )}
                </div>

                {entitlements && entitlements.length > 0 ? (
                    <List
                        dataSource={entitlements}
                        renderItem={(entitlement: IAddonEntitlement) => (
                            <List.Item
                                actions={[
                                    <Button
                                        key="edit"
                                        size="small"
                                        onClick={() => handleEditEntitlement(entitlement)}
                                    >
                                        Edit
                                    </Button>,
                                    <Button
                                        key="delete"
                                        size="small"
                                        danger
                                        onClick={() => handleDeleteEntitlement(entitlement)}
                                    >
                                        Remove
                                    </Button>,
                                ]}
                            >
                                <List.Item.Meta
                                    title={entitlement.feature?.name || entitlement.featureId}
                                    description={
                                        <Space>
                                            <Tag color="blue">{entitlement.feature?.featureType}</Tag>
                                            {getValueTypeTag(entitlement.valueType)}
                                            <span>Value: <strong>{entitlement.value}</strong></span>
                                        </Space>
                                    }
                                />
                            </List.Item>
                        )}
                    />
                ) : (
                    <Empty
                        description="No features assigned to this add-on"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                )}
            </Modal>

            {/* Add/Edit Entitlement Modal */}
            <Modal
                title={editingEntitlement ? 'Edit Feature Value' : 'Add Feature to Add-on'}
                open={isAddEntitlementModalOpen}
                onOk={handleSubmitEntitlement}
                onCancel={() => {
                    setIsAddEntitlementModalOpen(false);
                    entitlementForm.resetFields();
                }}
                confirmLoading={isCreatingEntitlement || isUpdatingEntitlement}
            >
                <Form form={entitlementForm} layout="vertical">
                    {!editingEntitlement && (
                        <Form.Item
                            name="featureId"
                            label="Feature"
                            rules={[{ required: true, message: 'Please select a feature' }]}
                        >
                            <Select placeholder="Select a feature">
                                {availableFeatures.map(feature => (
                                    <Select.Option key={feature.id} value={feature.id}>
                                        {feature.name} ({feature.featureType})
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}

                    {editingEntitlement && (
                        <Form.Item label="Feature">
                            <Input value={editingEntitlement.feature?.name} disabled />
                        </Form.Item>
                    )}

                    <Form.Item
                        name="valueType"
                        label="Value Type"
                        rules={[{ required: true, message: 'Please select a value type' }]}
                        extra={
                            <span style={{ color: '#999' }}>
                                <strong>Set:</strong> Replace plan value. <strong>Add:</strong> Add to plan value (for limits). <strong>Upgrade:</strong> Upgrade tier.
                            </span>
                        }
                    >
                        <Select>
                            <Select.Option value="set">Set (replace value)</Select.Option>
                            <Select.Option value="add">Add (add to limit)</Select.Option>
                            <Select.Option value="upgrade">Upgrade (upgrade tier)</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="value"
                        label="Value"
                        rules={[{ required: true, message: 'Please enter a value' }]}
                        extra={
                            <span style={{ color: '#999' }}>
                                For boolean features: "true" or "false". For limits: a number. For tiers: tier name.
                            </span>
                        }
                    >
                        <Input placeholder='e.g., "true", "500", "premium"' />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};
